/* eslint-disable no-restricted-syntax */
/** @jsxImportSource @emotion/react */
import dayjs from "dayjs";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { Accordion, AccordionBody, AccordionHeader, AccordionItem, Alert, Col, ColProps, FormGroup, FormText, Input, Label, Row } from "reactstrap";
import { optionsFlagged } from "../../actions/Layers/constants";
import { createBarcodeFilter } from "../../actions/Tenants/config/barcodeFilter";
import { BarcodeFilter, FormField, IFormField, RootConfigType } from "../../actions/Tenants/config/constantsTyped";
import { ISO6346_CONTAINER_NUMBER_HINT, onChangeCallbackRejects } from "../../actions/Tenants/config/onchange_callbacks/containerNumber";
import { getFilledArrayOrDefault } from "../../utils";
import { toast } from "../../utils/toast";
import DatePicker from "../Forms/DatePicker";
import FruitImages from "../scenes/AddItem/components/Summary/FruitImages";
import { CheckboxButtons, CheckboxButtonsManaged } from "./CheckboxButtons";
import DateRadioButtons from "./DateRadioButtons";
import { FileSelect } from "./FileSelect";
import { FtaDeviceField } from "./FtaDeviceField";
import { ImageRadioButtons } from "./ImageRadiButtons";
import { ImageViewerModal } from "./ImageViewerModal";
import LayerAutoComplete from "./LayerAutoComplete";
import LayerChildrenAutoComplete from "./LayerChildrenAutoComplete";
import MetaSelectCreatable from "./MetaSelectCreatable";
import NumberRadioButtons from "./NumberRadioButtons";
import NumericInputField from "./NumericInputField";
import { ObjectList } from "./ObjectList";
import { RadioButtons } from "./RadioButton";
import { RadioButtonManaged, SelectManaged } from "./SelectManaged";
import SingleSelect from "./SingleSelect";
import TextList from "./TextList";
import { TextQRcodeScanner } from "./TextQRCodeScan";
import { useDebounceForObject } from "./utils";

const GridWrapper = ({ children }: PropsWithChildren) => (
    <Row className="justify-content-start">{children}</Row>
);

const Wrapper = ({ children }: PropsWithChildren) => (
    <div>{children}</div>
);

const GridItemWrapper = ({ children, lg }: PropsWithChildren & Pick<ColProps, "lg">) => (
    <Col lg={lg}>{children}</Col>
);

const ItemWrapper = ({ children }: PropsWithChildren) => (
    <div className="pb-2">{children}</div>
);

export const selectAllOnFocus = (event) => event.target.select();

export interface MetaFormProps {
    setValue: (field: string, value: unknown) => void,
    setObject?: (nextObject: object) => void,
    onDebounce?: (field: string, value: unknown) => void,
    object: object,
    meta: Array<any>,
    grid?: boolean,
    config: RootConfigType,
    extra_context?: { // * pass extra context properties without setting them on the main object (e.g. to only show avocado options on check field, while fruit type is not stored on check but on layer)
        fruit_type?: string,
        fruit_variety?: string,
    }
    disabled?: boolean
    debounce_time?: number
    groupFields?: boolean
    withSearch?: boolean
    validationBaseline?: object
}

interface UpdateFieldProps {
    field: string,
    value: any,
    object?: object
}

function validatePattern(pattern: string, value: string) {
    if (pattern) {
        // eslint-disable-next-line security/detect-non-literal-regexp
        return (new RegExp(pattern)).test(value);
    }

    return true;
}

const LAYOUT_FIELD_TYPES = new Set(["alert", "header", "span", "element"]);

export function handlePatternValidation(item, object) {
    if (item?.pattern && object[item.name]) {
        return validatePattern(item.pattern, object[item.name]);
    }

    return true;
}

export type MetaFormValidationOptions = {
    // Stored values at the time the form opened. An onchange validator does not
    // reject a field that still equals its baseline, so an existing voyage label
    // can stay until that field is changed.
    baseline?: object;
};

export function metaFormIsValid(meta_form: IFormField[] = [], object: object, show_toast = true, options?: MetaFormValidationOptions) {
    const valueFields = meta_form.filter((item) => item && !LAYOUT_FIELD_TYPES.has(item.type));

    const missing_field = valueFields.find((item) => {
        if (item?.mandatory) {
            // For fta-device-field, use data_key instead of name
            const fieldKey = (item as any).type === "fta-device-field" ? (item as any).data_key : item.name;
            if (Array.isArray(object[fieldKey])) {
                if (object[fieldKey].length === 0) return true;
            } else if (!object[fieldKey] && object[fieldKey] !== false && object[fieldKey] !== 0) return true;
        }

        return false;
    });

    const invalid_value = valueFields
        .map((item) => ({ ...item, matchesPattern: handlePatternValidation(item, object) }))
        .find((item) => item.invalid_hint && !item.matchesPattern);

    if (invalid_value && invalid_value.invalid_hint) {
        if (show_toast) {
            toast.error(`${invalid_value.invalid_hint}`);
        }

        return false;
    }

    const callbackInvalid = valueFields.find((item) => onChangeCallbackRejects(
        item.onchange_callback,
        item.name ? object[item.name] : undefined,
        options?.baseline && item.name && item.name in options.baseline ? { value: options.baseline[item.name] } : undefined,
    ));

    if (callbackInvalid) {
        if (show_toast) {
            toast.error(callbackInvalid.invalid_hint || ISO6346_CONTAINER_NUMBER_HINT);
        }

        return false;
    }

    if (missing_field) {
        if (show_toast) {
            toast.error(`please fill in the ${missing_field.label}`);
        }

        return false;
    }

    return true;
}

export default function MetaForm({
    meta,
    setValue,
    setObject,
    onDebounce = () => null,
    object,
    grid = false,
    config,
    extra_context = {},
    disabled = false,
    debounce_time = 1000,
    groupFields = false,
    withSearch = false,
    validationBaseline,
}: MetaFormProps) {
    const [search, setSearch] = useState<string>("");
    const filteredFields = useMemo(
        () => (search ? meta?.filter((f) => f.type !== "header" && `${f.label + f.description + f.name} `.toLowerCase().includes(search.toLowerCase())) || [] : meta),
        [meta, search]
    );

    const TheWrapper = grid ? GridWrapper : Wrapper;
    const TheItemWrapper = grid ? GridItemWrapper : ItemWrapper;
    const min = false;
    const max = false;

    const debouncedSetValue = useDebounceForObject(setValue, onDebounce, debounce_time);

    const fields = filteredFields
        .filter((i) => i)
        .map((i) => {
            // * By default just update field
            let update = ({ field, value }: UpdateFieldProps) => debouncedSetValue(field, value);

            if (typeof i.onchange_callback === "function") {
                update = ({ field, value, object }: UpdateFieldProps) => i.onchange_callback({
                    setValue: debouncedSetValue,
                    setObject,
                    field,
                    value,
                    object
                });
                // * Configured, but predefined, onchange_callback
            } else if (typeof i.onchange_callback === "string") {
                const callback = config.onchange_callbacks[i.onchange_callback];

                if (typeof callback === "function") {
                    update = ({ field, value, object }: UpdateFieldProps) => callback({
                        setValue: debouncedSetValue,
                        setObject,
                        field,
                        value,
                        object
                    });
                } else {
                    console.error(`callback ${i.onchange_callback} not found`); // eslint-disable-line no-console
                }
            }

            return { ...i, update };
        }).map((i) => ({
            ...i,
            setValue: (field, value) => i.update({ field, value, object })
        })).map((i) => {
            if (i.inherit_ripening_classes_from_fruit_config) {
                i.options = config.get_fruit_types().flatMap((fruitType) => getFilledArrayOrDefault(fruitType.ripening_fields)
                    .filter((ripeningField) => ripeningField.fieldname_getter === i.inherit_ripening_classes_from_fruit_config)
                    .flatMap((ripeningField) => getFilledArrayOrDefault(ripeningField.categories)
                        .map((option) => ({ ...option, fruit_type: fruitType.value }))));
            }

            // * default_today: pre-fill date fields with today's date (yyyy-mm-dd) unless a default is already set
            if (i.default_today && (i.default_value === undefined || i.default_value === "")) {
                i.default_value = dayjs().format("YYYY-MM-DD");
            }

            return i;
        });

    const fieldsHaveDefaultValue = fields.filter((x) => x.default_value !== undefined && x.default_value !== "");

    useEffect(() => {
        // * Make a complete object
        const objectWithAllFieldsUpdated = fieldsHaveDefaultValue.reduce((acc, field) => {
            if (acc[field.name] === undefined || acc[field.name] === "") {
                return { ...acc, [field.name]: field.default_value };
            }

            return acc;
        }, object);

        // * Now call field.update with the complete object so that onchange callbacks properly work
        fieldsHaveDefaultValue.map((field) => {
            if ((object[field.name] === undefined || object[field.name] === "")) {
                field.update({ field: field.name, value: field.default_value, object: objectWithAllFieldsUpdated });
            }

            return null;
        });

    }, [fieldsHaveDefaultValue.map((i) => `${i.name}-${i.default_value}`).join(",")]);

    const getBarcodeFilter = (i): BarcodeFilter => {
        if (i.overwrite_barcode_template) {
            const filter = createBarcodeFilter(i.barcode_template ? [i.barcode_template] : []);
            const formatsToSupport = i.barcode_formats;

            return { filter, formatsToSupport };
        }

        return config?.barcode_filter;
    };

    const groupedFields = useMemo(() => {
        if (!groupFields) return fields;

        const groups: { id: string; title: string; fields: FormField[], isDeprecatedGroup?: boolean }[] = [];
        fields.forEach((f) => {
            if (f.type === "header") {
                const groupId = `group-${groups.length}`;
                groups.push({
                    id: groupId,
                    title: f.label,
                    fields: [],
                    isDeprecatedGroup: f.name === "deprecated_fields",

                });
            } else if (groups?.length) {
                groups[groups.length - 1].fields.push(f);
            } else {
                groups.push({
                    id: `group-${groups.length}`,
                    title: "",
                    fields: [f]
                });
            }
        });

        return groups;
    }, [fields, groupFields]);

    const [collapsedItems, setCollapsedItems] = useState<string[]>([groupedFields?.find((i) => i.isDeprecatedGroup)?.id || ""]);

    // Helper function to check if a field value is empty
    const isFieldEmpty = (value: any): boolean => {
        if (Array.isArray(value)) {
            return value.length === 0;
        }
        return !value && value !== false;
    };

    // Helper function to format label with mandatory asterisk (only red if field is empty)
    const formatLabel = (label: string | undefined, mandatory: boolean | undefined, fieldValue: any): any => {
        if (!label) return label;
        if (mandatory) {
            const isEmpty = isFieldEmpty(fieldValue);
            return (
                <>
                    {label}
                    <span className={isEmpty ? "text-danger ms-1" : "ms-1"}>*</span>
                </>
            );
        }
        return label;
    };

    const renderField = (i, index) => {
        // * Extra context used to pass data to the managed options hook for filtering options, but not data we want to store in the form object
        const item_extra_context = { ...extra_context, defect_position: i.defect_position, defect_severity: i.defect_severity };
        const fieldValue = object?.[i.name];
        const formattedLabel = formatLabel(i.label, i.mandatory, fieldValue);
        const callbackInvalid = onChangeCallbackRejects(
            i.onchange_callback,
            fieldValue,
            validationBaseline && i.name && i.name in validationBaseline ? { value: validationBaseline[i.name] } : undefined,
        );

        return (
            // Key by the field's stable name, not its array index. With an index
            // key, any change to the set of visible fields (a conditionally-shown
            // field toggling on a re-render) shifts every later field's key and
            // REMOUNTS it — which destroys in-field React state. That silently
            // closed the fruit-image CameraModal mid-capture: the offline
            // connectivity monitor re-renders this form every 5-7s, and a remount
            // on one of those ticks reset <ManualImages>'s modalState. A stable
            // name keeps each field's identity across such re-renders.
            // The type disambiguates a name that is configured twice, e.g. a
            // managed select with a plain-text fallback.
            <TheItemWrapper key={i.name ? `${i.name}__${i.type}` : index} lg={i.lg} >
                {i.type === "element" && (
                    <div>{i.el}</div>
                )}

                {/* Layout spacer: renders nothing but reserves the grid column so
                    the next field wraps to a new row. Use `lg` to size the gap. */}
                {i.type === "span" && (
                    <div aria-hidden="true" />
                )}

                {i.type === "header" && (
                    <FormGroup className="d-flex border-bottom align-items-center justify-content-between">
                        <h6 className="opacity-80 fw-bold">{i.label}</h6>

                        <ImageViewerModal label={i?.label} options={i?.options} />
                    </FormGroup>
                )}

                {i.type === "alert" && (i.label || i.description) && (
                    <Alert color={i.color || "info"} className="mb-0">
                        {i.label}
                        {i.description && <div className={i.label ? "mt-1" : undefined}>{i.description}</div>}
                    </Alert>
                )}

                {i.type === "layer-select" && (
                    <FormGroup>
                        <Label for={i.name}>{formattedLabel}</Label>

                        <LayerAutoComplete
                            type={i.layer_type ? i.layer_type : null}
                            setLayer={(value) => i.setValue(i.name, value)}
                            layer={object?.[i.name]}
                            multi={i.multi}
                            creatable={i.creatable}
                        />

                        {i.description && <FormText className="d-block">{i.description}</FormText>}
                    </FormGroup>
                )}

                {i.type === "layer-select-children" && (
                    <FormGroup>
                        <Label>{formattedLabel}</Label>

                        <LayerChildrenAutoComplete
                            type={i.layer_type ? i.layer_type : null}
                            post_value={object?.[i.name]}
                            setValue={i.setValue}
                            field={i.name}
                            meta_options={i.meta_options}
                        />

                        {i.description && <FormText className="d-block">{i.description}</FormText>}
                    </FormGroup>
                )}

                {i.type === "select-creatable" && (
                    <FormGroup>
                        <Label for={i.name}>{formattedLabel}</Label>

                        <MetaSelectCreatable
                            type={i.layer_type ? i.layer_type : null}
                            post_value={object?.[i.name]}
                            setValue={i.setValue}
                            field={i.name}
                            creatable={i.creatable}
                        />

                        {i.description && <FormText className="d-block">{i.description}</FormText>}
                    </FormGroup>
                )}

                {i.type === "single-select" && (
                    <SingleSelect
                        setValue={i.setValue}
                        options={getFilledArrayOrDefault(i?.options, [])}
                        post_value={object?.[i.name]}
                        field={i.name}
                        label={formattedLabel}
                        description={i.description}
                        multi={i.multi}
                        formatOptionLabel={i.formatOptionLabel}
                        return_objects={i.return_objects}
                        return_array={i.return_array}
                        disabled={i.disabled || disabled}
                    />
                )}

                {i.type === "select-managed" && (
                    <SelectManaged
                        setValue={i.setValue}
                        object={object}
                        post_value={object?.[i.name] === undefined ? i.default_value : object?.[i.name]}
                        name={i.name}
                        options_name={i.options_name}
                        label={formattedLabel}
                        description={i.description}
                        multi={i.multi}
                        hide_when_no_options={i.hide_when_no_options}
                        extra_context={item_extra_context}
                        return_objects={i.return_objects}
                        return_array={i.return_array}
                        disabled={i.disabled || disabled}
                    />
                )}

                {i.type === "date-radiobutton" && (
                    <DateRadioButtons
                        setValue={(value) => i.setValue(i.name, value)}
                        value={object?.[i.name]}
                        label={formattedLabel}
                        description={i.description}
                        forward={typeof i.forward === "string" ? parseInt(i.forward, 10) : i.forward}
                        backward={typeof i.backward === "string" ? parseInt(i.backward, 10) : i.backward}
                        disable_future_dates={i.disable_future_dates}
                        disabled={i.disabled || disabled}
                    />
                )}

                {i.type === "image-radiobutton" && (
                    <FormGroup>
                        <Label for={i.name}>{formattedLabel}</Label>

                        <ImageRadioButtons
                            setValue={(value) => i.setValue(i.name, value)}
                            value={object?.[i.name]}
                            options={i.options}
                            aspectRatio={i.image_buttons_aspect_ratio}
                        />

                        {i.description && <FormText className="d-block">{i.description}</FormText>}
                    </FormGroup>
                )}

                {i.type === "number" && (
                    <FormGroup>
                        <Label for={i.name}>{formattedLabel}</Label>

                        <NumericInputField
                            field={i.name}
                            appendendStyle={i.appendend_style}
                            db_value={object?.[i.name]}
                            label={i.label}
                            disabled={i.disabled || disabled}
                            min={({ min, ...i }).min}
                            max={({ max, ...i }).max}
                            setValue={(_, value) => i.setValue(i.name, value)}
                            unity={i.unity}
                            invalid={typeof i.validate === "function" && object?.[i.name] && !i.validate(object?.[i.name])}
                        />

                        {i.description && <FormText className="d-block">{i.description}</FormText>}
                    </FormGroup>
                )}

                {i.type === "number-radio" && (
                    <NumberRadioButtons
                        setValue={(value) => i.setValue(i.name, value)}
                        value={object?.[i.name]}
                        label={formattedLabel}
                        description={i.description}
                        min={typeof i.min === "string" ? parseInt(i.min, 10) : (i.min ?? 0)}
                        max={typeof i.max === "string" ? parseInt(i.max, 10) : (i.max ?? 10)}
                        disabled={i.disabled || disabled}
                        options={i.options}
                    />
                )}

                {i.type === "range" && (
                    <FormGroup>
                        {i.label && <Label for={i.name}>{formattedLabel}</Label>}

                        <Input
                            type="range"
                            name={i.name}
                            id={i.name}
                            disabled={i.disabled}
                            value={object?.[i.name] || i.min || 0}
                            min={i.min || 0}
                            max={i.max || 100}
                            step={i.step || 0.001}
                            onChange={(e) => i.setValue(i.name, e.target.value)}
                            invalid={typeof i.validate === "function" && !!object?.[i.name] && !i.validate(object?.[i.name])}
                        />

                        {i.description && <FormText className="d-block">{i.description}</FormText>}
                    </FormGroup>
                )}

                {(i.type === "text" || i.type === "textarea" || i.type === "email") && (
                    <FormGroup>
                        {i.label && <Label for={i.name}>{formattedLabel}</Label>}

                        <Input
                            type={i.type || "text"}
                            name={i.name}
                            id={i.name}
                            disabled={i.disabled || disabled}
                            value={object?.[i.name] || ""}
                            maxLength={i.validDigitsNumber || ""}
                            onChange={(e) => i.setValue(i.name, e.target.value)}
                            invalid={(typeof i.validate === "function" && !!object?.[i.name] && !i.validate(object?.[i.name])) || callbackInvalid}
                            onFocus={selectAllOnFocus}
                            {...(i.textInputProps ?? {})}
                        />

                        {typeof i.validate === "function" && object?.[i.name] && !i.validate(object?.[i.name]) && i.validDigitsNumber && (
                            <FormText>
                                        Please make sure that the number consists of {i.validDigitsNumber} digits
                            </FormText>
                        )}

                        {callbackInvalid && (
                            <FormText className="text-danger">{i.invalid_hint || ISO6346_CONTAINER_NUMBER_HINT}</FormText>
                        )}

                        {i.description && <FormText className="d-block">{i.description}</FormText>}
                    </FormGroup>
                )}

                {i.type === "text-qrcode-scanner" && (
                    <TextQRcodeScanner
                        barcode_filter={getBarcodeFilter(i) as any}
                        item={{ ...i, label: formattedLabel }}
                        object={object}
                        setValue={i.setValue}
                        onFocus={selectAllOnFocus}
                    />
                )}

                {i.type === "fta-device-field" && (
                    <FtaDeviceField
                        item={{ ...i, label: formattedLabel }}
                        object={object}
                        setValue={i.setValue}
                        disabled={disabled}
                    />
                )}

                {(i.type === "radio" || i.type === "radio-flag") && (
                    <FormGroup>
                        <Label for={i.name}>{formattedLabel}</Label>

                        <div>
                            <RadioButtons
                                color="secondary"
                                options={i.type === "radio-flag" ? optionsFlagged : getFilledArrayOrDefault(i?.options)}
                                name={i.name}
                                current={object?.[i.name] === undefined ? i.default_value : object?.[i.name]}
                                onChange={(_, value) => i.setValue(i.name, value === undefined ? i.default_value : value)}
                                disabled={i.disabled || disabled}
                                default_value={i.default_value}
                            />
                        </div>

                        {i.description && <FormText className="d-block">{i.description}</FormText>}
                    </FormGroup>
                )}

                {i.type === "radio-managed" && (
                    <RadioButtonManaged
                        setValue={i.setValue}
                        object={object}
                        post_value={object?.[i.name]}
                        name={i.name}
                        label={formattedLabel}
                        description={i.description}
                        options_name={i.options_name}
                        default_value={i.default_value}
                        extra_context={item_extra_context}
                        disabled={i.disabled || disabled}
                    />
                )}

                {i.type === "checkbox-managed" && (
                    <CheckboxButtonsManaged
                        onChange={i.setValue}
                        object={object}
                        selectedOptions={object?.[i.name] || []}
                        name={i.name}
                        label={formattedLabel}
                        description={i.description}
                        options_name={i.options_name}
                        extra_context={item_extra_context}
                        return_objects={i.return_objects}
                        return_array={i.return_array}
                    />
                )}

                {i.type === "checkbox" && (
                    <FormGroup>
                        <div className="d-flex align-items-center mb-1">
                            <Label for={i.name}>{formattedLabel}</Label>

                            <ImageViewerModal label={i.label} options={i.options} />
                        </div>

                        <div>
                            <CheckboxButtons
                                selectedOptions={object?.[i.name] || []}
                                onChange={(value) => i.setValue(i.name, value)}
                                options={i.options}
                                color={i.color}
                                className="me-1 mb-1"
                                return_objects={i.return_objects}
                                return_array={i.return_array}
                            />
                        </div>

                        {i.description && <FormText className="d-block">{i.description}</FormText>}
                    </FormGroup>
                )}

                {i.type === "fruit-images" && (
                    <FruitImages avocado_id={(object as any).avocado_id} />
                )}

                {i.type === "inline-form" && (
                    <FormGroup>
                        <Label for={i.name}>{formattedLabel}</Label>

                        <MetaForm
                            setValue={(field: string, value: unknown) => {
                                const formValue = object?.[i.name] || {};
                                formValue[field] = value;
                                setValue(i.name, formValue);
                            }}
                            grid={grid}
                            config={config}
                            meta={i.fields}
                            object={object?.[i.name]}
                        />

                        {i.description && <FormText className="d-block">{i.description}</FormText>}
                    </FormGroup>
                )}

                {i.type === "object-list" && (
                    <ObjectList
                        title={i.label}
                        description={i.description}
                        name={i.name}
                        items={object?.[i.name] || []}
                        form={i.fields}
                        onChange={(arr) => setValue(i.name, arr)}
                        getItemTitle={i.getItemTitle}
                        validate={i.validate}
                        disabled={i.disabled}
                    />
                )}

                {i.type === "date-picker" && (
                    <FormGroup>
                        <Label for={i.name}>{formattedLabel}</Label>

                        <DatePicker
                            value={object?.[i.name]}
                            onChange={(value) => i.setValue(i.name, value)}
                            disabled={i.disabled || disabled}
                        />
                    </FormGroup>
                )}

                {i.type === "file-select" && (
                    <FormGroup>
                        <Label>{formattedLabel}</Label>

                        <FileSelect
                            accept={i.accept}
                            onSelect={(name, content) => i.setValue(i.name, { name, content })}
                            onSelectImage={(path) => i.setValue(i.name, path)}
                            upload_config_image={i.upload_config_image}
                            readFormat={i.readFormat}
                            fileObject={i.upload_config_image && typeof object?.[i.name] === "string"
                                ? { blob_name: object[i.name], image_url: null }
                                : (object as any)}
                        />

                        {i.description && <FormText className="d-block">{i.description}</FormText>}
                    </FormGroup>
                )}

                {i.type === "text-list" && (
                    <FormGroup>
                        <Label for={i.name}>{formattedLabel}</Label>

                        <TextList
                            value={object?.[i.name] || []}
                            onChange={(value) => setValue(i.name, value)}
                            field={i.field}
                            disabled={i.disabled}
                        />
                    </FormGroup>
                )}
            </TheItemWrapper>
        );
    };

    return (
        <div className="d-flex flex-column gap-5">
            {withSearch && <Input type="search" placeholder="Search fields by label or description" value={search} onChange={(e) => setSearch(e.target.value)} />}

            <TheWrapper>
                {(groupFields && !!groupedFields.length)
                    ? (
                        <Accordion
                            className="secondary"
                            open={groupedFields.filter((item) => !collapsedItems.includes(item.id)).map((item) => item.id)}
                            flush
                            toggle={(id) => {
                                if (collapsedItems.includes(id)) {
                                    setCollapsedItems(collapsedItems.filter((i) => i !== id));
                                } else {
                                    setCollapsedItems([...collapsedItems, id]);
                                }
                            }}
                        >
                            {groupedFields?.map((group, groupIndex) => (
                                group.title
                                    ? (
                                        <AccordionItem key={groupIndex} className="mb-4">
                                            <AccordionHeader className="bg-black" targetId={`group-${groupIndex}`}>{group?.title}</AccordionHeader>

                                            <AccordionBody accordionId={`group-${groupIndex}`}>
                                                {group?.fields.map(renderField)}
                                            </AccordionBody>
                                        </AccordionItem>
                                    )
                                    : group?.fields.map(renderField)))}
                        </Accordion>
                    )
                    : fields.map(renderField)
                }
            </TheWrapper>
        </div>
    );
}
